import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {compoundConditionOutputSchema} from '../../promptToCardAgentV2.schemas';
import {CompoundConditionCardScratchpadState} from '../../scratchpad/compoundConditionCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import validateCompoundConditionOutput from '../../utils/compoundConditions/validateCompoundConditionOutput';
import setRuleSignal from '../../utils/compoundConditions/setRuleSignal';
import setRuleOperator from '../../utils/compoundConditions/setRuleOperator';
import setRuleValue from '../../utils/compoundConditions/setRuleValue';
import {
  CompoundConditionNodeRule,
  CompoundConditionNodeSection,
} from 'common/src/modules/decisionTree/decisionTree.types';

const editCompoundConditionOutputToolSchema = z.object({
  outputId: z.number().describe('The id of the output to update'),
  output: compoundConditionOutputSchema,
});

export default function editCompoundConditionOutputTool() {
  async function execute(
    params: ToolExecuteParams<
      typeof editCompoundConditionOutputToolSchema,
      CompoundConditionCardScratchpadState
    >,
  ) {
    const {outputId, output} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<CompoundConditionCardScratchpadState>(
      agentScratchpad,
      NodeType.COMPOUND_CONDITION,
    );

    const currentOutput = validatedScratchpad.card.outputs.find((o) => o.id === outputId);

    if (!currentOutput) {
      return toolError(
        `Compound condition output ID ${outputId} was not found for card ID ${validatedScratchpad.card.id}.`,
      );
    }

    const validationError = validateCompoundConditionOutput(output, validatedScratchpad.card);

    if (validationError) {
      return toolError(validationError);
    }

    const sections: CompoundConditionNodeSection[] = [];

    for (const section of output.sections) {
      const rules: CompoundConditionNodeRule[] = [];

      for (const rule of section.rules) {
        const signalResult = setRuleSignal(
          rule.signal.value,
          rule.signal.name,
          rule.signal.categories ?? [],
          validatedScratchpad.card,
        );

        if (typeof signalResult === 'string') {
          return toolError(signalResult);
        }

        const operatorResult = setRuleOperator(rule.ruleOperator);

        if (typeof operatorResult === 'string') {
          return toolError(operatorResult);
        }

        const valueResult = setRuleValue(rule.ruleValue, rule.ruleOperator);

        if (typeof valueResult === 'string') {
          return toolError(valueResult);
        }

        rules.push({
          signal: signalResult,
          ruleOperator: operatorResult,
          ruleValue: valueResult,
        });
      }

      sections.push({rules});
    }

    currentOutput.name = output.name;
    currentOutput.sections = sections;

    return toolResult('Compound condition output updated');
  }

  return tool<typeof editCompoundConditionOutputToolSchema, CompoundConditionCardScratchpadState>({
    name: V2CardAgentToolName.EditCompoundConditionOutput,
    displayName: 'Updating compound condition output',
    description:
      'Update an existing output in a compound condition card. Replaces the output sections and rules with the provided data.',
    inputSchema: editCompoundConditionOutputToolSchema,
    execute,
  });
}
